import React, { useMemo } from "react";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, ResponsiveContainer, Legend,
} from "recharts";
import { Info } from "lucide-react";
import { pledgeCurve, effectiveApr } from "@/lib/botMath";
import Stat from "@/components/calculator/Stat";

const bot = (n, dec = 2) => (isFinite(n) ? `${Number(n).toLocaleString(undefined, { maximumFractionDigits: dec })} BOT` : "—");
const usd = (n) => (isFinite(n) ? `$${Math.round(n).toLocaleString()}` : "—");

/** What a BOT pledge becomes — rewards compounding on the pledge, principal
 *  unlocking underneath it, and the BOT price riding on top. */
export default function BotPledgeView({ pledge, ratePct, termDays, days, botPrice, monthlyAppn }) {
  const sim = useMemo(() => {
    const rows = pledgeCurve({ pledge, ratePct, termDays, days, botPrice, monthlyApprPct: monthlyAppn });
    const flat = pledgeCurve({ pledge, ratePct, termDays, days, compound: false, botPrice });
    const byDay = new Map(flat.map((r) => [r.day, r.balance]));
    const merged = rows.map((r) => ({ ...r, flat: byDay.get(r.day) ?? null }));
    const last = rows[rows.length - 1] || { balance: 0, rewards: 0, unlocked: 0, usd: 0, botPrice: 0 };
    const P = Number(pledge) || 0;
    return {
      rows: merged, last, P,
      apr: effectiveApr(ratePct),
      growthPct: P ? (last.rewards / P) * 100 : 0,
      usdNow: P * (Number(botPrice) || 0),
    };
  }, [pledge, ratePct, termDays, days, botPrice, monthlyAppn]);

  const D = Math.max(1, Math.round(Number(days) || 0));
  const term = Math.round(Number(termDays) || 0);

  return (
    <div className="space-y-5" data-testid="bot-pledge-view">
      <div className="grid sm:grid-cols-3 gap-4">
        <Stat label={`BOT after ${D} days`} value={bot(sim.last.balance, 2)} color="#0E7490"
          sub={`from ${bot(sim.P, 2)} at ${ratePct}%/day, compounding`} testid="bot-kpi-balance" />
        <Stat label="Rewards earned" value={bot(sim.last.rewards, 2)} color="#00863A"
          sub={`+${sim.growthPct.toFixed(0)}% in BOT · ${sim.apr.toFixed(0)}% a year at this rate`} testid="bot-kpi-rewards" />
        <Stat label="Worth at the end" value={usd(sim.last.usd)} color="#7C3AED"
          sub={`BOT $${Number(botPrice || 0).toFixed(2)} → $${sim.last.botPrice.toFixed(2)} at ${Number(monthlyAppn) || 0}%/mo`} testid="bot-kpi-usd" />
      </div>

      <div className="glass p-5" data-testid="bot-growth-chart">
        <div className="text-xs font-mono uppercase tracking-wider text-ink-3 mb-3">Your BOT over time</div>
        <div style={{ width: "100%", height: 290 }}>
          <ResponsiveContainer width="100%" height="100%" minWidth={0}>
            <AreaChart data={sim.rows} margin={{ left: 8, right: 8, top: 4 }}>
              <defs>
                <linearGradient id="botBal" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#0E7490" stopOpacity={0.35} /><stop offset="100%" stopColor="#0E7490" stopOpacity={0.02} />
                </linearGradient>
                <linearGradient id="botUnlocked" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#B45309" stopOpacity={0.22} /><stop offset="100%" stopColor="#B45309" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(15,18,38,0.06)" />
              <XAxis dataKey="day" tick={{ fontSize: 11 }} tickFormatter={(d) => `d${d}`} />
              <YAxis tick={{ fontSize: 11 }} width={58}
                tickFormatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(1)}k` : Number(v).toFixed(v < 10 ? 1 : 0))} />
              <RTooltip formatter={(v, name) => [bot(v, 4), name]} labelFormatter={(d) => `Day ${d}`} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Area type="monotone" dataKey="flat" name="If you took the rewards out" stroke="#94A3B8" strokeDasharray="5 4" fill="none" strokeWidth={1.5} />
              <Area type="monotone" dataKey="balance" name="Pledge + rewards" stroke="#0E7490" fill="url(#botBal)" strokeWidth={2.5} />
              {term > 0 && (
                <Area type="monotone" dataKey="unlocked" name="Principal unlocked" stroke="#B45309" fill="url(#botUnlocked)" strokeWidth={2} />
              )}
            </AreaChart>
          </ResponsiveContainer>
        </div>
        <p className="text-[11px] text-ink-3 mt-3 leading-relaxed" data-testid="bot-growth-note">
          {term > 0
            ? `A ${term}-day pledge unlocks ${bot(sim.P / term, 4)} of your own pledge every day — ${bot(sim.last.unlocked, 2)} of the ${bot(sim.P, 2)} by day ${D} — while the rewards keep running on the whole pledge. That is how your own ${term}-day orders behave.`
            : "Flexible has no lock and no unlock schedule: the whole pledge is yours to pull any day, at the lower rate."}
        </p>
      </div>

      <div className="glass p-5 overflow-x-auto" data-testid="bot-breakdown">
        <div className="text-xs font-mono uppercase tracking-wider text-ink-3 mb-3">Month by month</div>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[11px] font-mono uppercase tracking-wider text-ink-3">
              <th className="py-1.5 pr-4">Day</th>
              <th className="py-1.5 pr-4">Pledge + rewards</th>
              <th className="py-1.5 pr-4">Rewards to date</th>
              <th className="py-1.5 pr-4">Principal unlocked</th>
              <th className="py-1.5">Worth</th>
            </tr>
          </thead>
          <tbody>
            {sim.rows.filter((r) => r.day > 0 && (r.day % 30 === 0 || r.day === D)).map((r) => (
              <tr key={r.day} className="border-t" style={{ borderColor: "rgba(15,18,38,0.06)" }}>
                <td className="py-2 pr-4 font-mono text-xs text-ink-3">Day {r.day}</td>
                <td className="py-2 pr-4 font-mono font-semibold tabular-nums">{bot(r.balance, 2)}</td>
                <td className="py-2 pr-4 font-mono tabular-nums" style={{ color: "#00863A" }}>{bot(r.rewards, 2)}</td>
                <td className="py-2 pr-4 font-mono tabular-nums" style={{ color: "#B45309" }}>{term > 0 ? bot(r.unlocked, 2) : "—"}</td>
                <td className="py-2 font-mono font-semibold tabular-nums" style={{ color: "#7C3AED" }}>{usd(r.usd)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="glass p-4 flex items-start gap-3" data-testid="bot-disclaimer">
        <Info size={15} style={{ color: "#0E7490" }} className="flex-shrink-0 mt-0.5" />
        <p className="text-xs text-ink-2 leading-relaxed">
          Rates and the BOT price are <em>assumptions</em> — CaryPact can change a plan and BOT can fall as
          easily as it rises. A what-if for planning, not a promise, and not financial advice.
        </p>
      </div>
    </div>
  );
}
